import type { AnalyticsConfig, AnalyticsTabKey } from './analyticsConfig';
import type { AssociationRow } from './analyticsTypes';
import { getRowFieldValue } from './analyticsHelpers';

export type MetricFormat = 'integer' | 'decimal' | 'decimal3';

export type MetricDomain =
  | { kind: 'unit' }
  | { kind: 'nonNegative' }
  | { kind: 'bounded'; min: number; max: number }
  | { kind: 'auto' };

export interface MetricDefinition {
  id: string;
  field: string;
  label: string;
  format: MetricFormat;
  domain: MetricDomain;
  rankable: boolean;
  axis: boolean;
  aggregable: boolean;
  filterable: boolean;
}

type Perspective = 'byDisease' | 'byGene' | 'byVariant' | 'byDiseasePair';

function metric(
  partial: Omit<MetricDefinition, 'rankable' | 'axis' | 'aggregable' | 'filterable'> &
    Partial<Pick<MetricDefinition, 'rankable' | 'axis' | 'aggregable' | 'filterable'>>,
): MetricDefinition {
  return {
    rankable: true,
    axis: true,
    aggregable: true,
    filterable: false,
    ...partial,
  };
}

const SCORE: MetricDefinition = metric({
  id: 'normalized_score',
  field: 'normalized_score',
  label: 'Normalized score',
  format: 'decimal3',
  domain: { kind: 'unit' },
  filterable: true,
});

const PAPERS: MetricDefinition = metric({
  id: 'numPMIDsupportingAssociation',
  field: 'numPMIDsupportingAssociation',
  label: 'Publications (PMIDs)',
  format: 'integer',
  domain: { kind: 'nonNegative' },
});

const EI: MetricDefinition = metric({
  id: 'EI',
  field: 'EI',
  label: 'Evidence index (EI)',
  format: 'decimal',
  domain: { kind: 'unit' },
});

const CTS: MetricDefinition = metric({
  id: 'numCTs',
  field: 'numCTs',
  label: 'Clinical trials',
  format: 'integer',
  domain: { kind: 'nonNegative' },
});

const GENE_DSI: MetricDefinition = metric({
  id: 'geneDSI',
  field: 'geneDSI',
  label: 'Gene DSI',
  format: 'decimal',
  domain: { kind: 'bounded', min: 0.2, max: 0.8 },
});

const GENE_DPI: MetricDefinition = metric({
  id: 'geneDPI',
  field: 'geneDPI',
  label: 'Gene DPI',
  format: 'decimal',
  domain: { kind: 'unit' },
});

const VARIANT_DSI: MetricDefinition = metric({
  id: 'variantDSI',
  field: 'variantDSI',
  label: 'Variant DSI',
  format: 'decimal',
  domain: { kind: 'bounded', min: 0.2, max: 0.8 },
});

const VARIANT_DPI: MetricDefinition = metric({
  id: 'variantDPI',
  field: 'variantDPI',
  label: 'Variant DPI',
  format: 'decimal',
  domain: { kind: 'unit' },
});

const DISEASES_PER_GENE: MetricDefinition = metric({
  id: 'numDiseasesAssociatedToGene',
  field: 'numDiseasesAssociatedToGene',
  label: 'Diseases per gene',
  format: 'integer',
  domain: { kind: 'nonNegative' },
});

const DISEASES_PER_VARIANT: MetricDefinition = metric({
  id: 'numDiseasesAssociatedToVariant',
  field: 'numDiseasesAssociatedToVariant',
  label: 'Diseases per variant',
  format: 'integer',
  domain: { kind: 'nonNegative' },
});

const GENE_PLI: MetricDefinition = metric({
  id: 'genepLI',
  field: 'genepLI',
  label: 'Gene pLI',
  format: 'decimal',
  domain: { kind: 'unit' },
});

const JACCARD_GENES: MetricDefinition = metric({
  id: 'jaccard_genes',
  field: 'jaccard_genes',
  label: 'Jaccard index (genes)',
  format: 'decimal3',
  domain: { kind: 'unit' },
  filterable: true,
});

const JACCARD_VARIANTS: MetricDefinition = metric({
  id: 'jaccard_variants',
  field: 'jaccard_variants',
  label: 'Jaccard index (variants)',
  format: 'decimal3',
  domain: { kind: 'unit' },
});

const SHARED_GENES: MetricDefinition = metric({
  id: 'shared_genes',
  field: 'shared_genes',
  label: 'Shared genes',
  format: 'integer',
  domain: { kind: 'nonNegative' },
});

const SHARED_VARIANTS: MetricDefinition = metric({
  id: 'shared_variants',
  field: 'shared_variants',
  label: 'Shared variants',
  format: 'integer',
  domain: { kind: 'nonNegative' },
});

const SOKAL: MetricDefinition = metric({
  id: 'sokal',
  field: 'sokal',
  label: 'SOKAL similarity',
  format: 'decimal',
  domain: { kind: 'unit' },
});

function getPerspective(tab: AnalyticsTabKey, view: string): Perspective {
  if (tab === 'DDA') return 'byDiseasePair';
  if (tab === 'GDA' && (view === 'GENES' || view === 'VARIANTS')) return 'byGene';
  if (tab === 'VDA' && view === 'VARIANTS') return 'byVariant';
  return 'byDisease';
}

function baseMetricsFor(tab: AnalyticsTabKey, perspective: Perspective): MetricDefinition[] {
  if (tab === 'DDA' || perspective === 'byDiseasePair') {
    return [JACCARD_GENES, JACCARD_VARIANTS, SHARED_GENES, SHARED_VARIANTS, SOKAL];
  }

  if (tab === 'VDA') {
    if (perspective === 'byVariant') {
      return [SCORE, PAPERS, EI, DISEASES_PER_VARIANT];
    }
    return [SCORE, PAPERS, EI, VARIANT_DSI, VARIANT_DPI, DISEASES_PER_VARIANT];
  }

  // GDA
  if (perspective === 'byGene') {
    return [SCORE, PAPERS, EI];
  }
  return [SCORE, PAPERS, EI, GENE_DSI, GENE_DPI, CTS, DISEASES_PER_GENE, GENE_PLI];
}

function withFilterable(metrics: MetricDefinition[], filterScoreField: string): MetricDefinition[] {
  return metrics.map(entry => ({
    ...entry,
    filterable: entry.field === filterScoreField,
  }));
}

export function getMetricCatalog(config: AnalyticsConfig, view: string): MetricDefinition[] {
  const perspective = getPerspective(config.tab, view);
  return withFilterable(baseMetricsFor(config.tab, perspective), config.filterScoreField);
}

export function getMetricById(
  catalog: MetricDefinition[],
  id: string | undefined | null,
): MetricDefinition | undefined {
  if (!id) return undefined;
  return catalog.find(entry => entry.id === id || entry.field === id);
}

export function getMetricByField(
  catalog: MetricDefinition[],
  field: string | undefined | null,
): MetricDefinition | undefined {
  if (!field) return undefined;
  return catalog.find(entry => entry.field === field || entry.id === field);
}

export function filterMetricsByCapability(
  catalog: MetricDefinition[],
  capability: 'rankable' | 'axis' | 'aggregable',
): MetricDefinition[] {
  return catalog.filter(entry => entry[capability]);
}

export function hasNumericFieldData(data: AssociationRow[], field: string): boolean {
  return data.some(row => Number.isFinite(Number(getRowFieldValue(row, field))));
}

export function getAvailableMetrics(catalog: MetricDefinition[], data: AssociationRow[]): MetricDefinition[] {
  if (!data.length) return catalog;
  return catalog.filter(entry => hasNumericFieldData(data, entry.field));
}

export function getDomainScale(domain: MetricDomain): { min: number | 'auto'; max: number | 'auto' } {
  switch (domain.kind) {
    case 'unit':
      return { min: 0, max: 1 };
    case 'bounded':
      return { min: domain.min, max: domain.max };
    case 'nonNegative':
      return { min: 0, max: 'auto' };
    case 'auto':
    default:
      return { min: 'auto', max: 'auto' };
  }
}

export function formatMetricValue(value: number, format: MetricFormat): string {
  if (!Number.isFinite(value)) return '—';
  if (format === 'integer') return Math.round(value).toLocaleString('es-ES');
  if (format === 'decimal3') return value.toFixed(3);
  return value.toFixed(2);
}
